import {
  ShieldCheck,
  CheckCircle,
  DollarSign,
  Home,
  ThumbsUp,
  Ruler,
  CalendarClock,
  Hammer,
  Users,
  Wrench,
} from 'lucide-react';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/energy-efficient-windows.js';



export default function EnergyEfficientWindowsPage() {
  const service = {
    title: 'Energy-Efficient Windows',
    image: '/hero/best-energy-efficient-windows-services-in-los-angeles.webp',
    description:
      'Install energy-efficient windows with Modern Build. Reduce bills, increase comfort, enhance style. Free estimate: (323) 300-4130',
    link: '/services/energy-efficient-windows',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Energy Efficient Windows Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Energy-Efficient Windows | Free Estimate | Modern Build</title>
        <meta name="title" content="Energy-Efficient Windows | Free Estimate | Modern Build" />
        <meta
          name="description"
          content="Install energy-efficient windows with Modern Build. Reduce bills, increase comfort, enhance style. Free estimate: (323) 300-4130"
        />
        <meta
          name="keywords"
          content="energy-efficient windows, window replacement, low-e glass, double-pane windows, Los Angeles window contractor, Modern Build"
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/energy-efficient-windows" />

        <meta property="og:title" content="Energy-Efficient Windows | Free Estimate | Modern Build" />
        <meta
          property="og:description"
          content="Install energy-efficient windows with Modern Build. Reduce bills, increase comfort, enhance style. Free estimate: (323) 300-4130"
        />
        <meta
          property="og:image"
          content="https://modernbuildremodel.com/hero/best-energy-efficient-windows-services-in-los-angeles.webp"
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/services/energy-efficient-windows"
        />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Energy-Efficient Windows | Free Estimate | Modern Build" />
        <meta
          name="twitter:description"
          content="Install energy-efficient windows with Modern Build. Reduce bills, increase comfort, enhance style. Free estimate: (323) 300-4130"
        />
        <meta
          name="twitter:image"
          content="https://modernbuildremodel.com/hero/best-energy-efficient-windows-services-in-los-angeles.webp"
        />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Energy-Efficient Windows | Free Estimate | Modern Build',
                description:
                  'Reduce heat loss, lower your energy bills, and improve your home’s curb appeal with modern energy-efficient window installation in Los Angeles.',
                slug: '/services/energy-efficient-windows',
              })
            ),
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="energyEfficientWindows" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="WINDOW INSTALLATION SERVICES"
        title="Upgrade to Energy-Efficient Windows"
        background="white"
        description="We install high-performance, energy-saving windows that increase comfort, reduce energy costs, and elevate your home’s aesthetics."
        extraText="Choose from double-pane, low-E, and custom window options designed for the Southern California climate."
        phone="(323) 300-4130"
        image1="/split/window-replacement-service-los-angeles.webp"
        image2="/split/energy-efficient-window-installation-los-angeles.webp"
      />

      <Benefits
        title="Why Choose Modern Build for Energy-Efficient Windows?"
        subtitle="Professional installation and premium products"
        items={[
          {
            icon: <ShieldCheck className="w-8 h-8 text-primary" />,
            title: "Top-Tier Energy Ratings",
          },
          {
            icon: <CheckCircle className="w-8 h-8 text-primary" />,
            title: "Low-E & Double-Pane Options",
          },
          {
            icon: <Hammer className="w-8 h-8 text-primary" />,
            title: "Precision Installation Techniques",
          },
          {
            icon: <Ruler className="w-8 h-8 text-primary" />,
            title: "Custom-Fit for Your Home",
          },
          {
            icon: <Wrench className="w-8 h-8 text-primary" />,
            title: "Retrofit & New Construction",
            href: "/services/earthquake-retrofit",
          },
          {
            icon: <DollarSign className="w-8 h-8 text-primary" />,
            title: "Energy Savings Over Time",
            href: "/services/solar-panels",
          },
          {
            icon: <ThumbsUp className="w-8 h-8 text-primary" />,
            title: "Work Performed by Licensed, Bonded & Insured Installers",
            href: "/about-us",
          },
          {
            icon: <Home className="w-8 h-8 text-primary" />,
            title: "Boost Home Value & Comfort",
          },
        ]}
      />

      <ContentBlock
        subtitle="LOS ANGELES WINDOW EXPERTS"
        title="High-Performance Windows for Year-Round Comfort"
        description="Our energy-efficient windows help regulate indoor temperature, reduce noise, and protect against UV damage. Modern Build ensures every installation is sealed tight and built to last."
        extraText="Whether you're replacing old windows or installing new ones, our team handles everything from consultation to final inspection."
        buttonText="Request a Free Window Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Window Installation Process"
        subtitle="A seamless upgrade from consultation to completion"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Initial Consultation",
            description: "We assess your existing windows and discuss the best energy-efficient solutions for your home.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Custom Measurements",
            description: "Accurate measurements ensure a perfect fit for improved insulation and aesthetics.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Professional Installation",
            description: "Our team installs your windows efficiently while maintaining clean workspaces and minimal disruption.",
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Final Walkthrough",
            description: "We ensure everything is sealed, tested, and exceeds expectations before we wrap up.",
          },
        ]}
      />

      <Split
        subtitle="ENERGY-SAVING SOLUTIONS"
        title="Make Your Home More Efficient Today"
        background="white"
        description="Replacing outdated windows with high-performance options is one of the most cost-effective ways to improve your home’s comfort and energy use."
        extraText="Let Modern Build help you choose the perfect windows for your style, climate, and budget."
        phone="(323) 300-4130"
        image1="/split/modern-energy-saving-windows-los-angeles.webp"
        image2="/split/home-window-upgrade-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Get a Free Estimate on Energy-Efficient Windows"
        subtitle="Better comfort, lower bills, and a more beautiful home"
        buttonText="Book Your Free Estimate"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="energy-efficient-windows" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
